import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, ArrowRight } from "lucide-react";

const TRACKS = [
  { title: "Born From Broken", src: "/assets/born-from-broken.mp3", testId: "home-anthem-chip-bfb" },
  { title: "When a Child Speaks", src: "/assets/when-a-child-speaks.mp3", testId: "home-anthem-chip-child-speaks" },
  { title: "Architect of Ruins", src: "/assets/architect-of-ruins.mp3", testId: "home-anthem-chip-architect" },
];

const fmtTime = (s) => {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

export default function HomeAnthem() {
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(TRACKS[0]);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const ensureAudio = () => {
    if (!audioRef.current) {
      const audio = new Audio();
      audio.addEventListener("timeupdate", () => setProgress(audio.currentTime));
      audio.addEventListener("loadedmetadata", () => setDuration(audio.duration));
      audio.addEventListener("ended", () => {
        setPlaying(false);
        setProgress(0);
      });
      audioRef.current = audio;
    }
    return audioRef.current;
  };

  const select = (t) => {
    setCurrent(t);
    const audio = ensureAudio();
    audio.src = t.src;
    setProgress(0);
    setDuration(0);
    audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };

  const toggle = () => {
    const audio = ensureAudio();
    if (!audio.src) audio.src = current.src;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play().then(() => setPlaying(true)).catch(() => {});
    }
  };

  return (
    <section
      data-testid="home-anthem-player"
      className="border-b border-amber-500/20 bg-[#0D111B]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
        <div className="flex items-center gap-4 min-w-0">
          <button
            data-testid="home-anthem-toggle"
            onClick={toggle}
            aria-label={playing ? `Pause ${current.title}` : `Play ${current.title}`}
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-500 text-[#0A0D14] shadow-[0_0_24px_-6px_rgba(245,158,11,0.7)] hover:bg-amber-400 transition-colors"
          >
            {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
          </button>
          <div className="min-w-0">
            <p className="font-code text-[10px] uppercase tracking-[0.25em] text-amber-400/90">
              The Anthem of the Movement
            </p>
            <p className="font-display text-base sm:text-lg font-medium text-slate-50 truncate">
              {current.title}
            </p>
          </div>
          {playing && (
            <div className="hidden sm:flex items-end gap-[3px] h-5 shrink-0" aria-hidden="true">
              {[0, 1, 2, 3].map((b) => (
                <span
                  key={b}
                  className="eq-bar w-[3px] h-full bg-amber-400 rounded-sm"
                  style={{ animationDelay: `${b * 0.15}s` }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {TRACKS.map((t) => (
            <button
              key={t.testId}
              data-testid={t.testId}
              onClick={() => select(t)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors border ${
                current.testId === t.testId
                  ? "border-amber-500/60 bg-amber-500/15 text-amber-300"
                  : "border-white/10 text-slate-400 hover:border-amber-500/40 hover:text-slate-200"
              }`}
            >
              {t.title}
            </button>
          ))}
        </div>

        <div className="flex-1 min-w-[140px]">
          <div className="h-1 rounded-full bg-white/10 overflow-hidden">
            <div
              data-testid="home-anthem-progress"
              className="h-full bg-gradient-to-r from-amber-500 to-amber-300 transition-[width] duration-300"
              style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }}
            />
          </div>
          <div className="mt-1 flex justify-between font-code text-[10px] text-slate-500">
            <span>{fmtTime(progress)}</span>
            <span>{fmtTime(duration)}</span>
          </div>
        </div>

        <Link
          to="/contact"
          data-testid="home-anthem-hub-link"
          className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-amber-400 hover:text-amber-300 transition-colors"
        >
          Full Anthem Hub
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </section>
  );
}
